import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { BarChart3, Bot, User, Sparkles } from 'lucide-react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const kpis = [
  {
    label: 'Win Rate',
    value: '78%',
    color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  },
  {
    label: 'Ticket',
    value: '$12.4K',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  },
  {
    label: 'Margen',
    value: '34.2%',
    color: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400',
  },
];

const barHeights = [40, 65, 50, 80, 60, 90, 72]; // percentage heights

const leaderboard = [
  { name: 'María López', amount: '$48.2K' },
  { name: 'Carlos Ruiz', amount: '$41.7K' },
  { name: 'Ana Torres', amount: '$38.9K' },
];

const chatMessages = [
  {
    role: 'user' as const,
    content: '¿Cuál es el margen promedio de mis cotizaciones este mes?',
  },
  {
    role: 'assistant' as const,
    content:
      'Tu margen promedio este mes es del 32.5%, un 4% más que el mes anterior. Las cotizaciones de infraestructura TI tienen el mayor margen (38%).',
  },
];

const capabilityPills = [
  'Análisis de márgenes',
  'Sugerencias de pricing',
  'Detección de anomalías',
];

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */

export function ControlSection() {
  return (
    <section id="control" className="relative py-24 sm:py-32 overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-violet-600/[0.04] dark:bg-violet-600/[0.06] rounded-full blur-[150px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ---- Header ---- */}
        <ScrollReveal className="text-center mb-16">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-violet-100 dark:bg-violet-900/40 px-3 py-1 text-xs font-semibold text-violet-700 dark:text-violet-300 mb-4">
            <BarChart3 className="w-3.5 h-3.5" />
            Visibilidad Total
          </span>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Todo bajo control.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-500 to-cyan-500">
              Datos e IA para decidir mejor.
            </span>
          </h2>

          <p className="max-w-2xl mx-auto text-gray-500 dark:text-gray-400">
            Dashboards en tiempo real y un asistente IA que entiende tu negocio. Pregunta lo que
            quieras en lenguaje natural.
          </p>
        </ScrollReveal>

        {/* ---- Two columns ---- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
          {/* Left — Analytics mini-dashboard */}
          <ScrollReveal direction="left">
            <AnalyticsDashboard />
          </ScrollReveal>

          {/* Right — AI Chat preview */}
          <ScrollReveal direction="right" delay={0.15}>
            <ChatMock />
          </ScrollReveal>
        </div>

        {/* ---- Capability pills ---- */}
        <ScrollReveal
          delay={0.3}
          className="mt-14 flex flex-wrap items-center justify-center gap-3"
        >
          {capabilityPills.map((pill) => (
            <span
              key={pill}
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
            >
              <Sparkles className="w-3.5 h-3.5 text-violet-500" />
              {pill}
            </span>
          ))}
        </ScrollReveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Analytics mini-dashboard                                           */
/* ------------------------------------------------------------------ */

function AnalyticsDashboard() {
  const barsRef = useRef<HTMLDivElement>(null);
  useInView(barsRef, { once: true, margin: '-60px' });

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/50 p-6 shadow-lg dark:shadow-gray-900/50 space-y-6">
      {/* KPI pills */}
      <div className="flex flex-wrap gap-3">
        {kpis.map((kpi) => (
          <span
            key={kpi.label}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${kpi.color}`}
          >
            {kpi.value} {kpi.label}
          </span>
        ))}
      </div>

      {/* Mini bar chart */}
      <div ref={barsRef} className="flex items-end gap-2 h-32">
        {barHeights.map((h, i) => (
          <motion.div
            key={i}
            className="flex-1 rounded-t-md bg-gradient-to-t from-violet-500 to-cyan-400"
            initial={{ height: 0 }}
            whileInView={{ height: `${h}%` }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: i * 0.08, ease: 'easeOut' }}
          />
        ))}
      </div>

      {/* Leaderboard */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-3">
          Top 3 vendedores
        </p>
        <ul className="space-y-2">
          {leaderboard.map((row, i) => (
            <li
              key={row.name}
              className="flex items-center justify-between rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-[10px] font-bold text-violet-700 dark:text-violet-300">
                  {i + 1}
                </span>
                <span className="text-sm text-gray-700 dark:text-gray-300">{row.name}</span>
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {row.amount}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Chat mock                                                          */
/* ------------------------------------------------------------------ */

function ChatMock() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [visibleMessages, setVisibleMessages] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    if (visibleMessages >= chatMessages.length) return;

    const delay = visibleMessages === 0 ? 400 : 1200;
    const timer = setTimeout(() => {
      setVisibleMessages((prev) => prev + 1);
    }, delay);

    return () => clearTimeout(timer);
  }, [isInView, visibleMessages]);

  return (
    <div
      ref={ref}
      className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/80 dark:backdrop-blur-sm overflow-hidden shadow-lg dark:shadow-gray-900/50 h-full flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700/80">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-white">Asistente IA</p>
          <p className="text-xs text-green-600 dark:text-green-400">En línea</p>
        </div>
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-500/10 dark:to-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/30 whitespace-nowrap">
          Powered by Claude
        </span>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 space-y-4 min-h-[320px]">
        {chatMessages.slice(0, visibleMessages).map((msg, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
          >
            {msg.role === 'assistant' && (
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Bot className="w-3.5 h-3.5 text-white" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700'
              }`}
            >
              {msg.content}
            </div>
            {msg.role === 'user' && (
              <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                <User className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
              </div>
            )}
          </motion.div>
        ))}

        {/* Typing indicator */}
        {isInView && visibleMessages > 0 && visibleMessages < chatMessages.length && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Bot className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl px-4 py-3 flex gap-1">
              <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:0ms]" />
              <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:150ms]" />
              <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:300ms]" />
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
